import React, { useEffect } from "react";

import getRegionComplianceRules from "api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "api/requests/getDispensary";
import { getDispensaryDeals } from "api/requests/getDispensaryDeals";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { Deal } from "custom-types/Deals";
import { Dispensary } from "custom-types/Dispensary";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { dealsMetadata } from "lib/dispensaryMetadata";
import { useDispatch } from "redux/hooks";
import {
  COMPLIANCE_RULESET_LOAD,
  ComplianceRuleset,
} from "redux/reducers/complianceRules";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import generateDispensaryRedirect from "utils/generateDispensaryRedirect";
import { getCountryCodeHeader } from "utils/requestContext";
import useTrackDispensaryPageViewSplitEvent from "utils/split/useTrackDispensaryPageViewSplitEvent";

import DealsDisclaimer from "components/Dispensary/DealsDisclaimer";
import DealsSection from "components/Dispensary/DealsSection";
import DispensaryBreadcrumbs from "components/Dispensary/DispensaryBreadcrumbs";
import DispensaryHeader from "components/Dispensary/DispensaryHeader";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import PromotionsSection from "components/Dispensary/PromotionsSection";
import MetaTags from "components/MetaTags";

type DealsPageProps = {
  complianceRuleset: ComplianceRuleset;
  dispensary: Dispensary;
  deals: Deal[];
  totalDealsCount: number;
};

const DealsPage: React.FC<DealsPageProps> = ({
  complianceRuleset,
  dispensary,
  deals,
  totalDealsCount,
}) => {
  const dispatch = useDispatch();
  const countryCode = useDomainCountryCode();

  const { coverPhotoUrl, logoUrl, name } = dispensary;

  const { description, image, title } = dealsMetadata(
    coverPhotoUrl,
    logoUrl,
    name,
    countryCode,
  );

  useTrackDispensaryPageViewSplitEvent({
    page: "deals",
    tier: dispensary.featureTier,
  });

  useEffect(() => {
    if (complianceRuleset) {
      dispatch({
        complianceRuleset,
        type: COMPLIANCE_RULESET_LOAD,
      });
    }
  }, []);

  return (
    <>
      <MetaTags
        description={description}
        image={image}
        shouldShowAlternates={false}
        title={title}
      />
      <DispensaryHeader dispensary={dispensary} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.deals}
        dispensary={dispensary}
      />

      <div data-testid="deals__content" className="container py-xxl">
        <DealsSection
          initialDeals={deals}
          dispensary={dispensary}
          totalDealsCount={totalDealsCount}
        />
        <PromotionsSection
          timeZone={dispensary.timeZone}
          name={dispensary.name}
          id={dispensary.id}
          title="Promotions"
          subtitle=" Know before you go! These promos are available to claim in-store. Ask
          your budtender before you checkout!"
        />
        <DealsDisclaimer />
        <DispensaryBreadcrumbs dispensary={dispensary} />
      </div>
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<DealsPageProps>(
  async (context) => {
    const { query, req, resolvedUrl } = context;
    const { slug } = query;

    const dispensary = await getDispensary(slug as string);

    const domainCountryCode = getCountryCodeHeader(req.headers);

    const redirect = generateDispensaryRedirect(
      domainCountryCode,
      resolvedUrl,
      query,
      dispensary,
    );

    if (redirect) {
      return { redirect };
    }

    // fallback if redirect not generated
    if (!dispensary) {
      return {
        notFound: true,
      };
    }

    const [{ data: deals, metadata }, complianceRuleset] = await Promise.all([
      getDispensaryDeals(String(slug), 21, 0),
      getRegionComplianceRules(dispensary.state, dispensary.country),
    ]);

    return {
      props: {
        complianceRuleset,
        deals,
        dispensary,
        totalDealsCount: metadata.totalCount,
      },
    };
  },
);

export default DealsPage;
